import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { CURRENCIES, TIMEZONES } from "../lib/helpers";
import { PageHeader, Field, NativeSelect } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ClinicEditDialog } from "../components/ClinicEditDialog";
import { ClinicFeaturesDialog } from "../components/ClinicFeaturesDialog";
import { TeamPanel, enterClinic } from "../components/AccountSwitch";
import { useApp } from "../context/AppContext";

const BLANK = { clinic_name: "", owner_name: "", email: "", password: "", currency: "EUR", timezone: "Europe/Rome", language: "en", status: "active" };
const STATUSES = ["trial", "active", "past_due", "cancelled", "suspended"];

function PlanSettings() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/platform/settings").then((r) => setP(r.data)); }, []);
  const save = async () => {
    try { setP((await api.put("/platform/settings", { ...p, plan_amount: Number(p.plan_amount), trial_days: Number(p.trial_days) })).data); toast.success("Plan saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!p) return null;
  return (
    <div className="rounded-[var(--radius)] border bg-card p-6 grid sm:grid-cols-5 gap-4 items-end" data-testid="platform-plan-form">
      <Field label="Plan name" className="sm:col-span-2"><Input data-testid="plan-name-input" value={p.plan_name} onChange={(e) => setP({ ...p, plan_name: e.target.value })} /></Field>
      <Field label="Monthly price"><Input data-testid="plan-amount-input" type="number" step="0.01" value={p.plan_amount} onChange={(e) => setP({ ...p, plan_amount: e.target.value })} /></Field>
      <Field label="Currency"><NativeSelect testId="plan-currency-select" value={p.plan_currency.toUpperCase()} onChange={(v) => setP({ ...p, plan_currency: v.toLowerCase() })} options={CURRENCIES.map((c) => ({ value: c, label: c }))} /></Field>
      <Field label="Trial days"><Input data-testid="plan-trial-input" type="number" value={p.trial_days} onChange={(e) => setP({ ...p, trial_days: e.target.value })} /></Field>
      <Button className="rounded-full sm:col-span-5 justify-self-start" data-testid="plan-save-btn" onClick={save}>Save plan</Button>
    </div>
  );
}

export default function Platform() {
  const { user } = useApp();
  const role = user.platform_role;
  const owner = role === "owner", support = owner || role === "support";
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [featuring, setFeaturing] = useState(null);
  const [f, setF] = useState(BLANK);
  const load = () => api.get("/platform/clinics").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });

  const create = async () => {
    try { await api.post("/platform/clinics", f); toast.success("Clinic created"); setF(BLANK); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const patch = async (id, body) => {
    try { await api.patch(`/platform/clinics/${id}`, body); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (c) => {
    const name = window.prompt(`This permanently deletes "${c.name}", its staff logins, patients, bookings and all other data. It cannot be undone.\n\nType the clinic name to confirm (${c.name}):`);
    if (name === null) return;
    try { await api.delete(`/platform/clinics/${c.id}`, { data: { confirm_name: name } }); toast.success("Clinic deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Bloomwell HQ" title="Platform admin" />
      {owner && <PlanSettings />}
      {owner && <TeamPanel />}
      {(owner || role === "sales") && <div className="rounded-[var(--radius)] border bg-card p-6 space-y-4" data-testid="platform-create-form">
        <h2 className="text-lg font-semibold">Create a clinic</h2>
        <div className="grid sm:grid-cols-4 gap-4">
          <Field label="Clinic name"><Input data-testid="pc-clinic-input" value={f.clinic_name} onChange={set("clinic_name")} /></Field>
          <Field label="Owner name"><Input data-testid="pc-owner-input" value={f.owner_name} onChange={set("owner_name")} /></Field>
          <Field label="Owner email"><Input data-testid="pc-email-input" type="email" value={f.email} onChange={set("email")} /></Field>
          <Field label="Temporary password"><Input data-testid="pc-password-input" value={f.password} onChange={set("password")} /></Field>
          <Field label="Currency"><NativeSelect testId="pc-currency-select" value={f.currency} onChange={set("currency")} options={CURRENCIES.map((c) => ({ value: c, label: c }))} /></Field>
          <Field label="Time zone"><NativeSelect testId="pc-timezone-select" value={f.timezone} onChange={set("timezone")} options={TIMEZONES.map((c) => ({ value: c, label: c }))} /></Field>
          <Field label="Start as"><NativeSelect testId="pc-status-select" value={f.status} onChange={set("status")} options={[{ value: "active", label: "Active (no trial)" }, { value: "trial", label: "Free trial" }]} /></Field>
          <Button className="rounded-full self-end" data-testid="pc-create-btn" disabled={!f.clinic_name || !f.email || f.password.length < 8} onClick={create}>Create clinic</Button>
        </div>
      </div>}
      <div className="rounded-[var(--radius)] border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="platform-clinics-table">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b">
            <tr>{["Clinic", "Owner", "Status", "Free access", "Trial ends", "Staff", "Appts", ""].map((h) => <th key={h} className="text-left font-medium px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y">
            {list.map((c) => (
              <tr key={c.id} data-testid={`platform-clinic-${c.slug}`}>
                <td className="px-4 py-3"><div className="font-semibold">{c.name}</div><a className="text-xs text-primary" href={`/book/${c.slug}`} target="_blank" rel="noreferrer">/book/{c.slug}</a>
                  {support && <button type="button" className="ml-2 text-xs text-muted-foreground hover:text-primary underline" data-testid={`platform-edit-${c.slug}`} onClick={() => setEditing(c)}>Edit</button>}
                  {support && <button type="button" className="ml-2 text-xs text-muted-foreground hover:text-primary underline" data-testid={`platform-features-${c.slug}`} onClick={() => setFeaturing(c)}>Features</button>}
                  {support && (c.support_open ? <button type="button" className="ml-2 text-xs font-medium text-primary underline" data-testid={`platform-enter-${c.slug}`} onClick={() => enterClinic(c.id)}>Open clinic (support)</button>
                    : <span className="ml-2 text-[11px] text-muted-foreground" data-testid={`platform-enter-locked-${c.slug}`}>Support access not allowed</span>)}</td>
                <td className="px-4 py-3">{c.owner_email || "—"}</td>
                <td className="px-4 py-3">
                  <NativeSelect testId={`platform-status-${c.slug}`} value={c.status} className="h-8 text-xs" disabled={!support} onChange={(v) => patch(c.id, { status: v })} options={STATUSES.map((s) => ({ value: s, label: s }))} />
                </td>
                <td className="px-4 py-3">{c.is_default ? <span className="text-xs text-muted-foreground">Your clinic</span> : (
                  <label className="inline-flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" data-testid={`platform-free-${c.slug}`} disabled={!owner} checked={c.plan === "free"} onChange={(e) => patch(c.id, { free_access: e.target.checked })} />{c.plan === "free" ? "Free" : "Paid plan"}</label>)}</td>
                <td className="px-4 py-3 text-xs">{c.status === "trial" && c.trial_ends_at ? c.trial_ends_at.slice(0, 10) : "—"}</td>
                <td className="px-4 py-3">{c.staff}</td><td className="px-4 py-3">{c.appointments}</td>
                <td className="px-4 py-3 whitespace-nowrap">{!c.is_default && support && <>
                  {c.plan !== "free" && <Button size="sm" variant="ghost" data-testid={`platform-extend-${c.slug}`} onClick={() => patch(c.id, { extend_trial_days: 14 })}>+14 days trial</Button>}
                  {owner && ["cancelled", "suspended"].includes(c.status) && <Button size="sm" variant="ghost" className="text-destructive" data-testid={`platform-delete-${c.slug}`} onClick={() => remove(c)}>Delete clinic</Button>}
                </>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ClinicEditDialog clinic={editing} onClose={() => setEditing(null)} onDone={load} />
      <ClinicFeaturesDialog clinic={featuring} onClose={() => setFeaturing(null)} onDone={load} />
    </div>
  );
}
